// * IMPORTANT: This file was largely generated Claude. Do not
// * change it manually unless you know what you are doing.

import { useCallback, useEffect, useState } from "react";
import { PauseIcon, PlayIcon } from "lucide-react";

import { Row } from "@/components/Row";
import { Button } from "@/components/ui/button";

import { getScreenFromPathname, mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { cn } from "@/lib/utils";

const formatTime = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m.toString()}:${String(s).padStart(2, "0")}`;
};

export const AudioPlayer = ({
  audioRef,
  audioSrc,
  className,
  trackClassName,
}: {
  audioRef: React.RefObject<HTMLAudioElement>;
  audioSrc: string;
  className?: string;
  trackClassName?: string;
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };
    const onDurationChange = () => {
      setDuration(audio.duration || 0);
    };
    const onPlay = () => {
      setIsPlaying(true);
    };
    const onPause = () => {
      setIsPlaying(false);
    };
    const onEnded = () => {
      setIsPlaying(false);
    };

    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("durationchange", onDurationChange);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);

    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("durationchange", onDurationChange);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
    };
  }, [audioRef]);

  const togglePlayPause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    mixpanelUtils.trackButtonClick(
      "Play/Pause",
      getScreenFromPathname(location.pathname),
    );
    if (isPlaying) {
      audio.pause();
    } else {
      void audio.play();
    }
  }, [audioRef, isPlaying]);

  const handleSeek = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const audio = audioRef.current;
      if (!audio) return;
      mixpanelUtils.trackButtonClick(
        "Audio seek",
        getScreenFromPathname(location.pathname),
      );
      audio.currentTime = Number(e.target.value);
    },
    [audioRef],
  );

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <Row className={cn("items-center gap-3 rounded-lg pl-2", className)}>
      <audio ref={audioRef} src={audioSrc} preload="metadata" />
      <Button
        className="h-7 w-7 shrink-0 text-slate-700 hover:bg-transparent hover:text-slate-900"
        onClick={togglePlayPause}
        size="icon"
        variant="ghost"
      >
        {isPlaying ? (
          <PauseIcon className="h-4 w-4 fill-current" />
        ) : (
          <PlayIcon className="h-4 w-4 fill-current" />
        )}
      </Button>
      <span className="w-[72px] shrink-0 text-xs text-slate-500">
        {formatTime(currentTime)} / {formatTime(duration)}
      </span>
      <Row className="relative flex-1 items-center">
        <div
          className={cn(
            "h-1.5 w-full overflow-hidden rounded-full bg-slate-200",
            trackClassName,
          )}
        >
          <div
            className="h-full rounded-full bg-primary-900 transition-[width] duration-100"
            style={{ width: `${progress.toString()}%` }}
          />
        </div>
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={currentTime}
          onChange={handleSeek}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        />
      </Row>
    </Row>
  );
};
